import { ApplicationInsights, type ITelemetryItem } from '@microsoft/applicationinsights-web';

let appInsights: ApplicationInsights | null = null;

export interface TelemetryService {
  trackEvent(name: string, properties?: Record<string, string | number | boolean>): void;
  trackPageView(name: string, uri?: string): void;
  trackException(error: Error, properties?: Record<string, string>): void;
  flush(): void;
}

function redactString(value: string): string {
  let result = value;
  // Redact emails
  result = result.replace(
    /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi,
    '[REDACTED_EMAIL]',
  );
  // Redact bearer tokens and JWTs
  result = result
    .replace(/\bBearer\s+[A-Za-z0-9\-._~+/]+=*/g, '[REDACTED_TOKEN]')
    .replace(/\beyJ[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+/g, '[REDACTED_TOKEN]');
  return result;
}

function scrubPii(value: unknown): void {
  if (!value || typeof value !== 'object') return;

  if (Array.isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      if (typeof value[i] === 'string') {
        value[i] = redactString(value[i] as string);
      } else {
        scrubPii(value[i]);
      }
    }
    return;
  }

  const obj = value as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    const v = obj[key];
    if (typeof v === 'string') {
      obj[key] = redactString(v);
    } else {
      scrubPii(v);
    }
  }
}

function createPiiScrubber() {
  return (item: ITelemetryItem): boolean => {
    if (item.data) {
      scrubPii(item.data);
    }
    const baseData = (item as unknown as { baseData?: unknown }).baseData;
    if (baseData) {
      scrubPii(baseData);
    }
    return true;
  };
}

export function initTelemetry(connectionString: string): TelemetryService {
  if (appInsights) {
    return createService(appInsights);
  }

  if (!connectionString) {
    // Local dev without an instrumentation key: surface events on the console so
    // the ADR-004 event stream is verifiable (WI 763387). Dead-code-eliminated
    // from production builds; prod without a key stays a silent no-op.
    if (import.meta.env.DEV) {
      return createConsoleService();
    }
    return createNoOpService();
  }

  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      // Page views are tracked manually via trackPageView() to control naming.
      // enableAutoRouteTracking is disabled to avoid double-counting.
      enableAutoRouteTracking: false,
      disableFetchTracking: false,
      // Disable cross-origin correlation until API domains are known.
      // Re-enable with correlationHeaderDomains allowlist once deployed.
      enableCorsCorrelation: false,
    },
  });

  appInsights.loadAppInsights();
  appInsights.addTelemetryInitializer(createPiiScrubber());

  // Tag all events with app=aita custom property and cloud role
  appInsights.addTelemetryInitializer((item: ITelemetryItem) => {
    // Set cloud role name (casing per agreed contract)
    item.tags = item.tags ?? [];
    const tags = item.tags as unknown as Record<string, string>;
    tags['ai.cloud.role'] = 'AITA';

    // Set app custom property for dashboard filtering (WI 749639 J1)
    item.data = item.data ?? {};
    const data = item.data as Record<string, unknown>;
    data['app'] = 'aita';
    return true;
  });

  return createService(appInsights);
}

function createService(ai: ApplicationInsights): TelemetryService {
  return {
    trackEvent(name: string, properties?: Record<string, string | number | boolean>): void {
      ai.trackEvent({ name }, properties);
    },
    trackPageView(name: string, uri?: string): void {
      ai.trackPageView({ name, uri });
    },
    trackException(error: Error, properties?: Record<string, string>): void {
      ai.trackException({ exception: error }, properties);
    },
    flush(): void {
      ai.flush();
    },
  };
}

function createNoOpService(): TelemetryService {
  return {
    trackEvent(): void {},
    trackPageView(): void {},
    trackException(): void {},
    flush(): void {},
  };
}

/* eslint-disable no-console -- dev-only diagnostic sink, never bundled for prod */
function createConsoleService(): TelemetryService {
  return {
    trackEvent(name, properties): void {
      console.debug('[aita-telemetry] event', name, properties ?? {});
    },
    trackPageView(name, uri): void {
      console.debug('[aita-telemetry] pageView', name, uri ?? '');
    },
    trackException(error, properties): void {
      console.debug('[aita-telemetry] exception', error.message, properties ?? {});
    },
    flush(): void {},
  };
}
/* eslint-enable no-console */

/** @internal Test-only: reset singleton */
export function _resetTelemetry(): void {
  appInsights = null;
}

/** @internal Exported for unit testing — the raw PII scrub function */
export const _scrubPii = scrubPii;
